import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/lib/apiClient";
import { PermissionRole } from "src/constants";
import { keys } from "./keys";
import { MerchantUser, mapMerchantUser } from "./mappers";

/**
 * The team domain, replacing `merchantUsersSlice` and the manual
 * `dispatch(getMerchantUsers(...))` that followed each write on the Manage Team
 * page.
 *
 * Two bugs disappear here. The slice had no `.rejected` case, so a failed fetch
 * left `loading: true` forever and the page spun indefinitely — a query has an
 * error state by construction. And the slice formatted `date_created` for
 * display inside the reducer, baking a date format into cached data; the mapper
 * keeps the raw ISO string and the view formats it.
 */
export const useTeam = (locationId: number | undefined) =>
  useQuery<MerchantUser[]>({
    queryKey: keys.location.users(locationId ?? -1),
    enabled: !!locationId && locationId > 0,
    queryFn: async () => {
      const response = await apiClient.get(`merchant/${locationId}/users`);
      return (response.data ?? []).map(mapMerchantUser);
    },
  });

/** Every write invalidates exactly one key, replacing three manual refetches. */
const useTeamMutation = <TArgs extends { locationId: number }, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: keys.location.users(variables.locationId),
      });
    },
  });
};

export const useInviteUser = () =>
  useTeamMutation(
    async ({
      locationId,
      ...payload
    }: { locationId: number } & Record<string, unknown>) => {
      const response = await apiClient.post(`merchant/${locationId}/invite`, payload);
      return response.data;
    }
  );

export const useUpdateUserRole = () =>
  useTeamMutation(
    async ({
      locationId,
      userId,
      role,
    }: {
      locationId: number;
      userId: number;
      role: PermissionRole;
    }) => {
      const response = await apiClient.put(`merchant/${locationId}/user/role`, {
        user_id: userId,
        role,
      });
      return response.data;
    }
  );

export const useRemoveUser = () =>
  useTeamMutation(
    async ({ locationId, userId }: { locationId: number; userId: number }) => {
      await apiClient.delete(`merchant/${locationId}/user/${userId}`);
    }
  );

/**
 * GET user/verify — an on-demand existence check before sending an invite.
 * Deliberately NOT a query: it is a one-shot question asked in response to a
 * click, and caching the answer would be wrong.
 */
export const verifyUserEmail = async (email: string): Promise<boolean> => {
  const response = await apiClient.get<{ exists: boolean }>("user/verify", {
    params: { email },
  });
  return response.data.exists;
};
